/* Makes the drawer's tile menu a plain in-place disclosure.

   Dawn's MenuDrawer binds a click handler to every <summary> inside
   <menu-drawer>. For a nested details it adds `submenu-open` to the closest
   `.has-submenu` (the nav <ul>), which slides the whole drawer off-canvas
   expecting a slide-in submenu panel. We don't want that here, so we swallow
   the click during the CAPTURE phase on this wrapper — that runs before any
   listener on the <summary> itself, so Dawn's handler never sees it.

   stopPropagation() does not cancel default actions, so the browser still
   toggles the <details> natively. */
if (!customElements.get('menu-tiles-disclosure')) {
  customElements.define(
    'menu-tiles-disclosure',
    class MenuTilesDisclosure extends HTMLElement {
      connectedCallback() {
        if (this._bound) return;
        this._bound = true;

        this.details = this.querySelector('details');
        this.summary = this.querySelector('summary');
        if (!this.details || !this.summary) return;

        this.addEventListener(
          'click',
          (event) => {
            if (event.target.closest('summary') === this.summary) event.stopPropagation();
          },
          true
        );

        this.details.addEventListener('toggle', () => {
          this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
        });
      }
    }
  );
}

/* Loads the tile images off the critical path.

   The tiles live inside a closed <details>, which gives them no layout box,
   and Chrome does not defer loading="lazy" images that have no box — every
   page of the site was fetching all six before DOMContentLoaded. So
   header-menu-tiles.liquid ships the URLs in data-src/data-srcset and we
   promote them here instead:

     - on hover / focus / touch intent on any summary that reveals tiles,
       which lands well before the click, and
     - at idle after load, on connections that can spare it, so that a first
       open is instant.

   A details' own toggle event is the backstop, so opening the menu always
   loads the images even if neither of the above fired. */
(() => {
  const PENDING = 'img.menu-tiles__img[data-srcset]';

  // The mega panel and the drawer hold the same six images but ask for
  // different widths, so promoting both would fetch twelve files to show six.
  // Only the layout the viewport is actually using gets promoted; the other
  // one waits for its own toggle, which re-runs this and picks it up if the
  // viewport has since crossed the breakpoint.
  const activeLayout = () =>
    window.matchMedia('(min-width: 990px)').matches ? '.menu-tiles--mega' : '.menu-tiles--drawer';

  const promote = (priority) => {
    document.querySelectorAll(activeLayout()).forEach((tiles) => {
      tiles.querySelectorAll(PENDING).forEach((img) => {
        if (priority) img.fetchPriority = priority;
        img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;
        delete img.dataset.srcset;
        delete img.dataset.src;
      });
    });
  };

  const onIntent = () => promote('high');

  const bindIntent = () => {
    const summaries = new Set();

    document.querySelectorAll('.menu-tiles').forEach((tiles) => {
      let node = tiles.parentElement;
      while (node) {
        if (node.tagName === 'DETAILS') {
          // Not once: a viewport that crosses 990px later still needs the
          // other layout promoted, and promote() is a no-op once it is done.
          node.addEventListener('toggle', onIntent);
          const summary = node.querySelector('summary');
          if (summary) summaries.add(summary);
        }
        node = node.parentElement;
      }
    });

    // Not once: a hover that finds nothing pending must not burn the listener,
    // or a later viewport change would have nothing left to promote the other
    // layout. promote() is idempotent and costs one querySelectorAll when done.
    summaries.forEach((summary) => {
      ['pointerenter', 'focusin', 'touchstart'].forEach((type) => {
        summary.addEventListener(type, onIntent, { passive: true });
      });
    });
  };

  const idlePromote = () => {
    const conn = navigator.connection;
    if (conn && (conn.saveData || ['slow-2g', '2g', '3g'].includes(conn.effectiveType))) return;
    promote('low');
  };

  const scheduleIdle = () => {
    if ('requestIdleCallback' in window) requestIdleCallback(idlePromote, { timeout: 4000 });
    else setTimeout(idlePromote, 2500);
  };

  // This file is deferred, so the tiles are already parsed: bind before load
  // so an early hover still gets its head start.
  bindIntent();

  if (document.readyState === 'complete') scheduleIdle();
  else window.addEventListener('load', scheduleIdle, { once: true });
})();
